/**
 * FILTRD BRAND COLORS
 *
 * Single source of truth for all color values across the application.
 * These colors are theme-independent and should be used directly in components.
 *
 * For theme-aware colors (that change between nature/dark/light modes),
 * use the useTheme() hook and access theme tokens.
 *
 * Based on visual-style-guide.md
 */

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create rgba color with specified opacity
 * @param color - Hex color string (e.g., '#FFFFFF')
 * @param opacity - Opacity value between 0 and 1
 * @returns rgba string
 */
const alpha = (color: string, opacity: number): string => {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  // Handle existing rgba - extract and replace opacity
  if (color.startsWith('rgba')) {
    return color.replace(/[\d.]+\)$/g, `${opacity})`);
  }
  return color;
};

// ============================================================================
// COLOR PALETTE
// ============================================================================

export const colors = {
  // ==========================================================================
  // PRIMARY COLORS
  // ==========================================================================
  deepNavy: '#0F172A',
  pureWhite: '#FFFFFF',
  softWhite: '#F8FAFC',

  // ==========================================================================
  // ACCENT COLORS
  // ==========================================================================
  signalOrange: '#F97316',
  signalOrangeHover: '#EA580C',
  signalOrangeLight: '#FFF7ED',

  // ==========================================================================
  // SLATE SCALE (Secondary Colors)
  // ==========================================================================
  slate: {
    100: '#F1F5F9',
    200: '#E2E8F0',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
  },

  // ==========================================================================
  // SEMANTIC COLORS
  // ==========================================================================
  success: '#22C55E',
  error: '#EF4444',

  // ==========================================================================
  // ALPHA VARIANTS (Translucent Colors)
  // ==========================================================================
  whiteAlpha: {
    10: alpha('#FFFFFF', 0.1),
    20: alpha('#FFFFFF', 0.2),
    30: alpha('#FFFFFF', 0.3),
    50: alpha('#FFFFFF', 0.5),
    95: alpha('#FFFFFF', 0.95),
  },

  deepNavyAlpha: {
    95: alpha('#0F172A', 0.95),
  },

  blackAlpha: {
    10: alpha('#000000', 0.1),
  },

  orangeAlpha: {
    25: alpha('#F97316', 0.25),
  },

  // ==========================================================================
  // GRADIENTS
  // ==========================================================================
  gradients: {
    deepNavy: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
  },

  // ==========================================================================
  // SHADOWS
  // ==========================================================================
  shadows: {
    orange: `0 4px 12px ${alpha('#F97316', 0.25)}`,
  },

  // ==========================================================================
  // BORDERS
  // ==========================================================================
  borders: {
    whiteAlpha10: `1px solid ${alpha('#FFFFFF', 0.1)}`,
  },
};

// ============================================================================
// HELPER FUNCTIONS (Export for component use)
// ============================================================================

/**
 * Quick background color with opacity
 * @example bg(colors.deepNavy, 0.1) => 'rgba(15, 23, 42, 0.1)'
 */
export const bg = (color: string, opacity: number = 0.1) => alpha(color, opacity);

/**
 * Quick border with color and opacity
 * @example border(colors.pureWhite, 0.3) => '1px solid rgba(255, 255, 255, 0.3)'
 */
export const border = (color: string, opacity: number = 0.3, width: string = '1px') =>
  `${width} solid ${alpha(color, opacity)}`;

/**
 * Create alpha variant of any color
 * @example withOpacity(colors.signalOrange, 0.5) => 'rgba(249, 115, 22, 0.5)'
 */
export const withOpacity = (color: string, opacity: number) => alpha(color, opacity);
